/**
 * SharePointLibraryForm — Step 2: input the SharePoint library URL.
 *
 * Owns: live URL preview (debounced parse via `previewSharePointUrl`),
 * include_subfolders toggle, Save + Test Connection buttons. Save persists
 * `library_url` + `include_subfolders` on the project's SharePoint config.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useI18n } from "../../context/I18nContext";

export default function SharePointLibraryForm({
  projectId,
  isAdmin,
  cfg,
  setCfg,
  companyCreds,
  form,
  setForm,
}) {
  const { t } = useI18n();
  const [parsedPreview, setParsedPreview] = useState(() => (cfg ? {
    hostname: cfg.hostname,
    site_relative_url: cfg.site_relative_url,
    document_library: cfg.document_library,
    folder_path: cfg.folder_path,
  } : null));
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // Debounced URL preview
  useEffect(() => {
    const url = form.library_url?.trim();
    if (!url) { setParsedPreview(null); return; }
    const handle = setTimeout(async () => {
      try {
        const { previewSharePointUrl } = await import("../../lib/api");
        const r = await previewSharePointUrl(url);
        if (r.ok) setParsedPreview(r.parsed);
        else setParsedPreview({ error: r.error });
      } catch { setParsedPreview(null); }
    }, 400);
    return () => clearTimeout(handle);
  }, [form.library_url]);

  const onSave = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    setSaving(true);
    try {
      const { upsertSharePointConfig } = await import("../../lib/api");
      const r = await upsertSharePointConfig(projectId, form);
      setCfg(r);
      toast.success(t("success"));
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setSaving(false); }
  };

  const onTest = async () => {
    setTesting(true);
    try {
      const { testSharePointConnection } = await import("../../lib/api");
      const r = await testSharePointConnection(projectId);
      if (r.ok) toast.success(r.message || "Conexão OK");
      else toast.error(r.message || "Falha na conexão");
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setTesting(false); }
  };

  return (
    <form onSubmit={onSave} className="bg-white border border-zinc-200 rounded-md p-5 space-y-4">
      <div className="flex items-center gap-2 pb-3 border-b border-zinc-100">
        <span className="cs-label">step 2</span>
        <h4 className="font-heading text-sm font-semibold">{t("library_url")}</h4>
      </div>
      <div>
        <label className="cs-label block mb-1.5">{t("library_url")}</label>
        <input
          type="url" value={form.library_url} placeholder="https://contoso.sharepoint.com/sites/Projetos/Cronogramas"
          disabled={!isAdmin}
          onChange={(e) => setForm({ ...form, library_url: e.target.value })}
          className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm font-mono disabled:bg-zinc-100"
          data-testid="sp-library-url-input"
        />
        <p className="text-[11px] text-zinc-500 mt-1">{t("library_url_help")}</p>
      </div>

      {parsedPreview && !parsedPreview.error && (
        <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3" data-testid="sp-parsed-preview">
          <div className="cs-label !text-[10px] mb-2">{t("parsed_preview")}</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-[11px]">
            <div><span className="text-zinc-500">host:</span> <span className="text-zinc-900">{parsedPreview.hostname}</span></div>
            <div><span className="text-zinc-500">site:</span> <span className="text-zinc-900">{parsedPreview.site_relative_url}</span></div>
            <div><span className="text-zinc-500">library:</span> <span className="text-zinc-900">{parsedPreview.document_library}</span></div>
            <div><span className="text-zinc-500">folder:</span> <span className="text-zinc-900">{parsedPreview.folder_path || "/"}</span></div>
          </div>
        </div>
      )}
      {parsedPreview?.error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-xs text-red-700" data-testid="sp-parsed-error">{parsedPreview.error}</div>
      )}

      <label className="flex items-center gap-2 text-sm text-zinc-700">
        <input type="checkbox" checked={!!form.include_subfolders} disabled={!isAdmin}
          onChange={(e) => setForm({ ...form, include_subfolders: e.target.checked })}
          className="rounded text-cs-accent focus:ring-cs-accent"
          data-testid="sp-include-subfolders-input"
        />
        {t("include_subfolders")}
      </label>

      <div className="flex items-center justify-end gap-2 pt-2">
        {cfg?.status === "configured" && (
          <button type="button" onClick={onTest} disabled={testing || !companyCreds.configured}
            className="px-4 py-2 border border-zinc-300 rounded-md text-sm hover:bg-zinc-50 disabled:opacity-60"
            data-testid="sp-test-btn"
          >
            {testing ? "..." : t("test_connection")}
          </button>
        )}
        {isAdmin && (
          <button type="submit" disabled={saving || !companyCreds.configured || !!parsedPreview?.error || !form.library_url}
            className="px-4 py-2 bg-cs-accent text-white rounded-md text-sm font-semibold hover:bg-cs-accentHover disabled:opacity-60"
            data-testid="sp-save-btn"
          >
            {saving ? "..." : t("save_config")}
          </button>
        )}
      </div>
    </form>
  );
}
